import { inject, Injectable } from '@angular/core';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
  BehaviorSubject,
  catchError,
  concatMap,
  EMPTY,
  filter,
  first,
  map,
  Observable,
  of,
  switchMap,
  take,
  tap,
} from 'rxjs';

import { runtimeSelectors } from '@blaze-runtime/engine-data-access';
import { CanvasNamespaceId, isNavMenuItemsLayout, LayoutData } from '@blaze-runtime/engine-domain';

import {
  layoutFetched,
  layoutLoadingError,
  layoutReady,
} from '../+state/elements/elements.actions';
import { getLayoutIds } from '../+state/elements/elements.selectors';
import { LayoutsApiDataResponse, LayoutsApiService } from '../data/layouts-api.service';

import { LayoutNormalizerService } from './compat/layout-normalizer.service';
import { LayoutMigrationRunnerService } from './compat/migrations/layout-migration-runner.service';

@Injectable({
  providedIn: 'root',
})
export class LayoutLoadingService {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly layoutsApi = inject(LayoutsApiService);

  private readonly layoutNormalizer = inject(LayoutNormalizerService);
  private readonly layoutMigrationRunner = inject(LayoutMigrationRunnerService);
  private readonly loadedLayouts$$ = new BehaviorSubject<CanvasNamespaceId[]>([]);
  private readonly waitingFor$$ = new BehaviorSubject<CanvasNamespaceId[]>([]);

  get loadedLayoutIds$(): Observable<CanvasNamespaceId[]> {
    return this.loadedLayouts$$.asObservable();
  }

  get waitingForIds$(): Observable<CanvasNamespaceId[]> {
    return this.waitingFor$$.asObservable();
  }

  constructor() {
    this.store.select(getLayoutIds).subscribe((layoutIds) => {
      this.loadedLayouts$$.next(layoutIds);
    });

    // If failed or successful - remove from waiting list
    this.actions$.pipe(ofType(layoutReady, layoutLoadingError)).subscribe(({ layout }) => {
      this.waitingFor$$.next(this.waitingFor$$.value.filter((id) => id !== layout.pageId));
    });
  }

  /** Loads the layout if the layout is not present in the state */
  loadAndWait(layoutId: CanvasNamespaceId): Observable<CanvasNamespaceId> {
    this.markLayoutAsNeeded(layoutId);

    return this.loadedLayoutIds$.pipe(
      filter((ids) => ids.some((id) => id === layoutId)),
      take(1),
      map(() => layoutId),
    );
  }

  reloadLayout(layoutId: CanvasNamespaceId): Observable<LayoutData> {
    return this.loadLayout(layoutId);
  }

  /** Marks the layout as needed - in other words loads it if it's not present in the state */
  markLayoutAsNeeded(layoutId?: CanvasNamespaceId): void {
    // Don't download layout that's ready or being loaded
    if (layoutId === undefined || this.isLayoutReadyOrLoading(layoutId)) {
      return;
    }
    this.loadLayout(layoutId).subscribe();
  }

  /** Used to load the layout from the backend and apply the compatibility upgrades */
  getLayout(layoutId: CanvasNamespaceId | undefined): Observable<LayoutsApiDataResponse> {
    if (!layoutId) {
      throw new Error('Missing layout ID');
    }
    return this.fetchLayout(layoutId).pipe(
      map((layout) => {
        if (isNavMenuItemsLayout(layout)) {
          return layout;
        }
        this.layoutMigrationRunner.migrateLayoutMutably(layout);
        return layout;
      }),
      concatMap((layout) => {
        if (isNavMenuItemsLayout(layout)) {
          return of(layout);
        }
        return this.layoutNormalizer.normalize(layout);
      }),
    );
  }

  /**
   * Fetches layout definition from either an endpoint or an URL depending on whether the URL is provided.
   * @param layoutId - ID of the layout to fetch
   * @param layoutUrl - Optional layout URL from which to fetch the layout instead of the endpoint.
   */
  private fetchLayout(layoutId: CanvasNamespaceId | undefined): Observable<LayoutsApiDataResponse> {
    if (!layoutId) {
      throw new Error('Missing layout ID');
    }
    return this.store.select(runtimeSelectors.selectPageLayoutUrlById(layoutId)).pipe(
      first(),
      switchMap((layoutUrl) => {
        if (layoutUrl) {
          try {
            return this.layoutsApi.fetchLayoutFile(layoutUrl).pipe(
              catchError(() => {
                return this.layoutsApi.getLayout(layoutId);
              }),
            );
          } catch {
            return this.layoutsApi.getLayout(layoutId);
          }
        }
        return this.layoutsApi.getLayout(layoutId);
      }),
      catchError(() => {
        console.warn('Unable to download layout json file.');
        return this.layoutsApi.getLayout(layoutId);
      }),
    );
  }

  private isLayoutReadyOrLoading(layoutId: CanvasNamespaceId): boolean {
    return (
      this.loadedLayouts$$.value.includes(layoutId) || this.waitingFor$$.value.includes(layoutId)
    );
  }

  private loadLayout(layoutId: CanvasNamespaceId): Observable<LayoutData> {
    this.waitingFor$$.next([...this.waitingFor$$.value, layoutId]);

    return this.getLayout(layoutId).pipe(
      catchError((err) => {
        console.error('Layout loading error', err);
        const layout = {
          pageId: layoutId,
          uiLayer: { content: [] },
          actionLayer: { connections: [] },
          version: 0,
          aiLayout: null,
          jobId: null,
        } as LayoutData;
        this.store.dispatch(layoutLoadingError({ layout, err }));
        return EMPTY;
      }),
      // Store fetched layout in the state
      tap((layout) => {
        this.store.dispatch(layoutFetched({ layout }));
      }),
    );
  }
}
